/**
 * DadosDeCobrancaDialog.jsx — modal "Dados para o pagamento" (pacote de créditos e domínio).
 * Pura: recebe { vm, actions }. Contrato: components/billing/dadosDeCobranca.vm.js (@version 2).
 *   vm      → open, loading, busy, title, explanation, name, nameError, document, documentError,
 *             formError, methods[], method, methodHint, confirmLabel
 *   actions → onChangeName, onChangeDocument, onChangeMethod, onConfirm, onCancel
 * Meio de pagamento: radio só com 2+ meios; o texto acima do botão (methodHint) diz o que acontece
 * ao confirmar — QR na tela (PIX) ou ida ao ambiente do banco (cartão). Nunca o nome do gateway.
 * Estilo só via `sx` + CSS vars (var(--*)), no padrão do sub-dialog "Editar nome" (shell/SettingsView.jsx).
 * A View não valida nada: o documento chega mascarado e os erros chegam prontos do hook.
 */
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography, Alert,
  CircularProgress, Stack, RadioGroup, Radio, FormControlLabel, FormLabel, FormControl,
} from '@mui/material';

const paperSx = {
  bgcolor: 'var(--surface)',
  color: 'var(--text-primary)',
  borderRadius: 'var(--radius-card)',
  boxShadow: 'var(--shadow-hover)',
  backgroundImage: 'none',
};

const fieldSx = {
  '& .MuiInputLabel-root': { color: 'var(--text-secondary)' },
  '& .MuiInputLabel-root.Mui-focused': { color: 'var(--color-primary)' },
  '& .MuiInputLabel-root.Mui-error': { color: 'var(--color-error)' },
  '& .MuiOutlinedInput-root': {
    borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 'var(--fs-body2)',
    '& fieldset': { borderColor: 'var(--divider)' },
    '&:hover fieldset': { borderColor: 'var(--color-primary-light)' },
    '&.Mui-focused fieldset': { borderColor: 'var(--color-primary)' },
    '&.Mui-error fieldset': { borderColor: 'var(--color-error)' },
  },
  '& .MuiFormHelperText-root': { color: 'var(--text-secondary)' },
  '& .MuiFormHelperText-root.Mui-error': { color: 'var(--color-error)' },
};

export default function DadosDeCobrancaDialog({ vm, actions }) {
  const travado = !!vm?.loading || !!vm?.busy;

  return (
    <Dialog
      open={!!vm?.open}
      onClose={() => { if (!vm?.busy) actions?.onCancel?.(); }}
      maxWidth="xs"
      fullWidth
      aria-labelledby="dados-cobranca-title"
      PaperProps={{ sx: paperSx }}
    >
      <DialogTitle id="dados-cobranca-title" sx={{
        px: 3, pt: 3, pb: .5, fontWeight: 'var(--fw-bold)', fontSize: 'var(--fs-h6)',
      }}>
        {vm?.title}
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 0 }}>
        <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)', mb: 2 }}>
          {vm?.explanation}
        </Typography>

        {vm?.loading ? (
          <Stack alignItems="center" sx={{ py: 3 }}>
            <CircularProgress size={24} sx={{ color: 'var(--color-primary)' }} />
          </Stack>
        ) : (
          <Stack spacing={1}>
            <TextField
              autoFocus
              fullWidth
              label="Nome ou razão social"
              value={vm?.name ?? ''}
              onChange={(e) => actions?.onChangeName?.(e.target.value)}
              disabled={travado}
              error={!!vm?.nameError}
              helperText={vm?.nameError || ' '}
              autoComplete="organization"
              inputProps={{ maxLength: 255 }}
              sx={fieldSx}
            />
            <TextField
              fullWidth
              label="CPF ou CNPJ"
              value={vm?.document ?? ''}
              onChange={(e) => actions?.onChangeDocument?.(e.target.value)}
              disabled={travado}
              error={!!vm?.documentError}
              helperText={vm?.documentError || ' '}
              // CNPJ novo tem LETRA: teclado de texto, nunca inputMode="numeric". Sem maxLength: colar " 12.ABC…" com
              // espaço seria CORTADO pelo navegador antes da máscara limpar — quem limita é mascararDocumento (14).
              inputProps={{ autoCapitalize: 'characters', spellCheck: false, 'aria-label': 'CPF ou CNPJ' }}
              sx={fieldSx}
            />

            {(vm?.methods?.length ?? 0) > 1 && (
              <FormControl component="fieldset" disabled={travado}>
                <FormLabel component="legend" sx={{
                  typography: 'body2', fontWeight: 'var(--fw-medium)', color: 'var(--text-primary)',
                  '&.Mui-focused': { color: 'var(--text-primary)' },
                }}>
                  Meio de pagamento
                </FormLabel>
                <RadioGroup
                  row
                  name="meio-de-pagamento"
                  value={vm?.method ?? 'Pix'}
                  onChange={(e) => actions?.onChangeMethod?.(e.target.value)}
                >
                  {vm.methods.map((m) => (
                    <FormControlLabel
                      key={m.id}
                      value={m.id}
                      label={m.label}
                      control={<Radio sx={{
                        color: 'var(--text-secondary)',
                        '&.Mui-checked': { color: 'var(--color-primary)' },
                      }} />}
                      sx={{ '& .MuiFormControlLabel-label': { typography: 'body2', color: 'var(--text-primary)' } }}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            )}

            {vm?.methodHint && (
              <Typography variant="body2" sx={{
                px: 1.5, py: 1, borderRadius: 'var(--radius-sm)',
                bgcolor: 'var(--color-primary-soft)', color: 'var(--text-primary)',
              }}>
                {vm.methodHint}
              </Typography>
            )}
          </Stack>
        )}

        {vm?.formError && (
          <Alert severity="error" sx={{
            mt: 1, mb: 1, borderRadius: 'var(--radius-sm)',
            bgcolor: 'var(--tint-error-bg)', color: 'var(--text-primary)',
            '& .MuiAlert-icon': { color: 'var(--color-error)' },
          }}>
            {vm.formError}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
        <Button
          onClick={() => actions?.onCancel?.()}
          disabled={!!vm?.busy}
          sx={{
            minHeight: 40, px: 2, textTransform: 'none', fontWeight: 'var(--fw-medium)',
            color: 'var(--text-secondary)', borderRadius: 'var(--radius-sm)',
            '&:hover': { bgcolor: 'var(--bg-page)', color: 'var(--text-primary)' },
            '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: 2 },
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={() => actions?.onConfirm?.()}
          disabled={travado}
          startIcon={vm?.busy ? <CircularProgress size={16} sx={{ color: 'var(--on-primary)' }} /> : null}
          sx={{
            minHeight: 40, px: 2.5, textTransform: 'none', fontWeight: 'var(--fw-bold)',
            bgcolor: 'var(--color-primary)', color: 'var(--on-primary)', borderRadius: 'var(--radius-sm)',
            '&:hover': { bgcolor: 'var(--color-primary-dark)' },
            '&.Mui-disabled': { bgcolor: 'var(--color-primary-light)', color: 'var(--on-primary)', opacity: .8 },
            '&:focus-visible': { outline: '2px solid var(--color-primary-dark)', outlineOffset: 2 },
          }}
        >
          {vm?.busy ? 'Aguarde…' : vm?.confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
